import React, { useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import AddOptionMappingDialog from '../AddOptionMappingDialog/AddOptionMappingDialog';
import DataTable from '../../../Shared/Components/DataTable/DataTable';
import { OfferOptionUsageType } from '../../Types/offerOptionsMappingsTypes';
import { offerOptionUsageTypeLabels } from '../../Helpers/offerOptionUsageTypeLabels';
import { useDeleteOfferOptionsMappingMutation } from '../../Service/offerOptionsMappingsApi';
import { OfferOptionMappingsSectionProps } from './OfferOptionMappingsSectionTypes';

const OfferOptionMappingsSection = ({
  offerId,
  offerOptionTypes,
  mappings,
  mappingsLoading,
  offerOptionsLoading,
  refetchMappings,
}: OfferOptionMappingsSectionProps) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [deleteMapping] = useDeleteOfferOptionsMappingMutation();

  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;
    await Promise.all(selected.map((id) => deleteMapping(id)));
    setSelected([]);
    refetchMappings();
  };

  if (!offerId) {
    return (
      <Typography color="text.secondary">Opcje oferty są dostępne po zapisaniu oferty.</Typography>
    );
  }

  return (
    <Box>
      <Button variant="contained" onClick={() => setAddDialogOpen(true)} sx={{ mb: 2 }}>
        Dodaj opcję
      </Button>
      <Button
        variant="outlined"
        color="error"
        onClick={handleDeleteSelected}
        disabled={selected.length === 0}
        sx={{ mb: 2, ml: 2 }}
      >
        Usuń zaznaczone
      </Button>
      <AddOptionMappingDialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          refetchMappings();
        }}
        offerId={offerId}
        optionTypes={(offerOptionTypes || []).map((type) => ({
          ...type,
          offerOptionsValues: (type.offerOptionsValues || [])
            .filter((v) => v.value !== undefined)
            .map((v) => ({
              ...v,
              value: v.value as string,
            })),
        }))}
        defaultUsageType={OfferOptionUsageType.Mandatory}
      />

      {mappingsLoading || offerOptionsLoading ? (
        <CircularProgress />
      ) : (
        <DataTable
          columns={[
            { key: 'offerOptionsTypeName', label: 'Typ opcji' },
            { key: 'offerOptionsValueName', label: 'Wartość' },
            {
              key: 'usageType',
              label: 'Rodzaj',
              render: (row) => offerOptionUsageTypeLabels[row.usageType],
            },
            { key: 'description', label: 'Opis' },
            { key: 'amount', label: 'Kwota' },
            { key: 'currency', label: 'Waluta' },
          ]}
          data={mappings || []}
          isLoading={false}
          getRowId={(row) => row.offerOptionMappingId}
          selected={selected}
          onSelectionChange={setSelected}
        />
      )}
    </Box>
  );
};

export default OfferOptionMappingsSection;
